import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Card, Button, Input, Label } from "../components/ui";
import {
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  ArrowRight,
  ListFilter,
  FileDown,
  Calendar,
  X
} from "lucide-react";
import api from "../services/api";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

export default function Dashboard() {
  const navigate = useNavigate();
  const [ordensRaw, setOrdensRaw] = useState([]);
  const [displayList, setDisplayList] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(null);
  
  const hojeISO = new Date().toISOString().split('T')[0];

  const [dataInicio, setDataInicio] = useState(hojeISO);
  const [dataFim, setDataFim] = useState(hojeISO);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportLimit, setExportLimit] = useState(50);

  const [stats, setStats] = useState({
    andamento: 0,
    finalizadas: 0,
    canceladas: 0,
    totalNoPeriodo: 0,
  });

  useEffect(() => {
    carregarDados();
  }, [dataInicio, dataFim]);

  const carregarDados = async () => {
    try {
      const res = await api.get("/os");
      let ordens = res.data;

      if (dataInicio || dataFim) {
        ordens = ordens.filter(os => {
          const parts = os.data_abertura.split('/');
          const dOS = new Date(parts[2], parts[1] - 1, parts[0]);
          const dIni = dataInicio ? new Date(dataInicio) : new Date(1900, 0, 1);
          const dFim = dataFim ? new Date(dataFim) : new Date(2100, 0, 1);
          dFim.setHours(23, 59, 59);
          return dOS >= dIni && dOS <= dFim;
        });
      }

      setOrdensRaw(ordens);

      let andamento = 0, finalizadas = 0, canceladas = 0, totalNoPeriodo = 0;

      ordens.forEach((os) => {
        const status = os.status || (os.data_encerramento ? "Finalizada" : "Andamento");
        if (status === "Andamento") andamento++;
        if (status === "Finalizada") finalizadas++;
        if (status === "Cancelada") canceladas++;
        totalNoPeriodo++; 
      });

      setStats({ andamento, finalizadas, canceladas, totalNoPeriodo });
      
      if (selectedFilter) {
         const filtradas = ordens.filter((os) => {
            const status = os.status || (os.data_encerramento ? "Finalizada" : "Andamento");
            if (selectedFilter === "Hoje") return true;
            return status === selectedFilter;
         });
         setDisplayList(filtradas);
      }
    } catch (e) {
      toast.error("Falha ao carregar dashboard.");
    }
  };

  const handleFilterClick = (tipo) => {
    if (selectedFilter === tipo) {
      setSelectedFilter(null);
      setDisplayList([]);
      return;
    }

    setSelectedFilter(tipo);
    const filtradas = ordensRaw.filter((os) => {
      const status = os.status || (os.data_encerramento ? "Finalizada" : "Andamento");
      if (tipo === "Hoje") return true;
      return status === tipo;
    });
    setDisplayList(filtradas);
  };

  const baixarCSV = async () => {
    const loadingToast = toast.loading("Gerando planilha Excel...");
    try {
      const response = await api.get("/relatorio/csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Relatorio_OS_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      toast.success("Download concluído!", { id: loadingToast });
    } catch (e) {
      toast.error("Erro ao gerar CSV", { id: loadingToast });
    }
  };

  const baixarPDFGeral = async () => {
    setIsExportModalOpen(false);
    const loadingToast = toast.loading("Gerando PDF...");
    try {
      const payload = {
        empresa_nome: localStorage.getItem("@up2_empresa_nome") || "",
        empresa_logo: localStorage.getItem("@up2_empresa_logo") || "",
        limite: exportLimit
      };
      const response = await api.post("/relatorio/pdf-geral", payload, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Relatorio_Geral_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      toast.success("PDF baixado!", { id: loadingToast });
    } catch (e) {
      toast.error("Erro ao gerar PDF", { id: loadingToast });
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in pb-10">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Dashboard</h2>
          <p className="text-slate-500 text-sm">
            Clique nos cards para visualizar os detalhes abaixo.
          </p>
        </div>

        <div className="flex gap-2 w-full lg:w-auto">
          <Button onClick={() => setIsExportModalOpen(true)} variant="outline" className="flex-1 lg:w-auto border-slate-300">
            <FileDown size={16} className="mr-2" /> PDF
          </Button>
          <Button
            onClick={baixarCSV}
            variant="outline"
            className="flex-1 lg:w-auto bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
          >
            <Download size={16} className="mr-2" /> Excel
          </Button>
        </div>
      </div>

      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col sm:flex-row md:flex-col lg:flex-row items-center gap-4">
        <div className="flex items-center gap-2 text-slate-500 font-medium">
          <Calendar size={18} />
          <span className="text-sm">Filtrar Período:</span>
        </div>
        <div className="grid grid-cols-1 sm:flex flex-row items-center gap-2 w-full sm:w-auto">
          <Input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} className="h-9 text-xs" />
          <span className="text-slate-400 hidden sm:block">até</span>
          <Input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} className="h-9 text-xs" />
          {(dataInicio || dataFim) && (
            <button onClick={() => {setDataInicio(""); setDataFim("");}} className="flex p-2 bg-slate-200 hover:bg-slate-300 rounded-full text-red-400 transition-colors justify-center">
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card
          className={`p-6 border-l-4 cursor-pointer hover:shadow-md transition-all active:scale-95 ${selectedFilter === "Andamento" ? "border-l-amber-500 bg-amber-50/50 scale-[1.02]" : "border-l-amber-500"}`}
          onClick={() => handleFilterClick("Andamento")}
        >
          <div className="flex justify-between gap-2 items-start">
            <div>
              <p className="text-sm font-medium text-slate-500">Em Andamento</p>
              <h3 className="text-3xl font-bold text-slate-900 mt-2">{stats.andamento}</h3>
            </div>
            <div className={`p-3 rounded-full ${selectedFilter === "Andamento" ? "bg-amber-500 text-white" : "bg-amber-100 text-amber-600"}`}>
              <Clock size={20} />
            </div>
          </div>
        </Card>

        <Card
          className={`p-6 border-l-4 cursor-pointer hover:shadow-md transition-all active:scale-95 ${selectedFilter === "Finalizada" ? "border-l-emerald-500 bg-emerald-50/50 scale-[1.02]" : "border-l-emerald-500"}`}
          onClick={() => handleFilterClick("Finalizada")}
        >
          <div className="flex justify-between gap-2 items-start">
            <div>
              <p className="text-sm font-medium text-slate-500">Finalizadas</p>
              <h3 className="text-3xl font-bold text-slate-900 mt-2">{stats.finalizadas}</h3>
            </div>
            <div className={`p-3 rounded-full ${selectedFilter === "Finalizada" ? "bg-emerald-500 text-white" : "bg-emerald-100 text-emerald-600"}`}>
              <CheckCircle size={20} />
            </div>
          </div>
        </Card>

        <Card
          className={`p-6 border-l-4 cursor-pointer hover:shadow-md transition-all active:scale-95 ${selectedFilter === "Cancelada" ? "border-l-red-500 bg-red-50/50 scale-[1.02]" : "border-l-red-500"}`}
          onClick={() => handleFilterClick("Cancelada")}
        >
          <div className="flex justify-between gap-2 items-start">
            <div>
              <p className="text-sm font-medium text-slate-500">Canceladas</p>
              <h3 className="text-3xl font-bold text-slate-900 mt-2">{stats.canceladas}</h3>
            </div>
            <div className={`p-3 rounded-full ${selectedFilter === "Cancelada" ? "bg-red-500 text-white" : "bg-red-100 text-red-600"}`}>
              <XCircle size={20} />
            </div>
          </div>
        </Card>

        <Card
          className={`p-6 border-l-4 cursor-pointer hover:shadow-md transition-all active:scale-95 ${selectedFilter === "Hoje" ? "border-l-blue-500 bg-blue-50/50 scale-[1.02]" : "border-l-blue-500"}`}
          onClick={() => handleFilterClick("Hoje")}
        >
          <div className="flex justify-between gap-2 items-start">
            <div>
              <p className="text-sm font-medium text-slate-500">Abertas no Período</p>
              <h3 className="text-3xl font-bold text-slate-900 mt-2">{stats.totalNoPeriodo}</h3>
            </div>
            <div className={`p-3 rounded-full ${selectedFilter === "Hoje" ? "bg-blue-500 text-white" : "bg-blue-100 text-blue-600"}`}>
              <FileText size={20} />
            </div>
          </div>
        </Card>
      </div>

      {/* DRILL DOWN LIST */}
      {selectedFilter && (
        <div className="animate-in slide-in-from-top-4 duration-500">
          <Card className="overflow-hidden border-slate-300 shadow-lg">
            <div className="bg-slate-900 px-6 py-4 border-b flex justify-between items-center text-white">
              <div className="flex items-center gap-2">
                <ListFilter size={18} />
                <h3 className="font-bold">
                  Mostrando:{" "}
                  <span>
                    {selectedFilter === "Andamento" ? "Em Andamento" : selectedFilter === "Finalizada" ? "Finalizadas" : selectedFilter === "Cancelada" ? "Canceladas" : "Abertas no Período"}
                  </span>{" "}
                  ({displayList.length})
                </h3>
              </div>
              <button onClick={() => setSelectedFilter(null)} className="text-xs font-bold text-slate-400 hover:text-white uppercase tracking-wider">FECHAR</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left min-w-[600px]">
                <thead className="bg-slate-50 text-slate-600 border-b">
                  <tr>
                    <th className="px-6 py-3 font-medium uppercase text-xs">Nº da OS</th>
                    <th className="px-6 py-3 font-medium uppercase text-xs">Cliente</th>
                    <th className="px-6 py-3 font-medium uppercase text-xs">Data</th>
                    <th className="px-6 py-3 font-medium uppercase text-xs">Total</th>
                    <th className="px-6 py-3 font-medium text-right uppercase text-xs">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {displayList.map((os) => (
                    <tr key={os.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 font-mono font-bold text-slate-700">{os.codigo_os}</td>
                      <td className="px-6 py-4 text-slate-900 font-medium">{os.cliente_nome}</td>
                      <td className="px-6 py-4 text-slate-500">{os.data_abertura}</td>
                      <td className="px-6 py-4 font-medium">R$ {(os.valor_total || 0).toFixed(2).replace(".", ",")}</td>
                      <td className="px-6 py-4 text-right">
                        <button onClick={() => navigate(`/editar/${os.id}`)} className="text-brand hover:underline font-bold flex items-center gap-1 ml-auto">
                          Ver <ArrowRight size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      <div className="mt-8 pt-8 border-t border-slate-200">
        <h3 className="text-lg font-bold text-slate-800 mb-4">Ações Rápidas</h3>
        <div className="flex flex-col sm:flex-row gap-4">
          <Button onClick={() => navigate("/nova")} className="w-full sm:w-auto bg-brand text-white">Criar Nova OS</Button>
          <Button onClick={() => navigate("/relatorio")} variant="outline" className="w-full sm:w-auto">Acessar Relatório Completo</Button>
        </div>
      </div>

      {isExportModalOpen && createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in px-4">
          <Card className="p-6 w-full max-w-sm shadow-2xl animate-in zoom-in-95">
            <h3 className="text-xl font-bold text-slate-900 mb-2">Exportar Relatório PDF</h3>
            <p className="text-sm text-slate-500 mb-6">Quantos registros recentes deseja exportar?</p>
            
            <div className="mb-8">
              <Label>Selecione a Quantidade</Label>
              <select 
                className="w-full h-11 rounded-md border border-slate-300 bg-slate-50 px-3 text-sm outline-none focus:ring-2 focus:ring-brand"
                value={exportLimit}
                onChange={e => setExportLimit(parseInt(e.target.value))}
              >
                <option value={5}>Últimas 5 ordens</option>
                <option value={10}>Últimas 10 ordens</option>
                <option value={20}>Últimas 20 ordens</option>
                <option value={50}>Últimas 50 ordens</option>
                <option value={100}>Últimas 100 ordens</option>
                <option value={0}>Todas (Sem limites)</option>
              </select>
            </div>

            <div className="flex gap-3">
              <Button onClick={() => setIsExportModalOpen(false)} variant="outline" className="flex-1 border-none text-slate-500">Cancelar</Button>
              <Button onClick={baixarPDFGeral} className="flex-1 bg-slate-900 text-white shadow-lg">Gerar PDF</Button>
            </div>
          </Card>
        </div>,
        document.body
      )}
    </div>
  );
}